import {inject, Injectable} from '@angular/core';
import {UserService} from "./user.service";
import {PushNotifications} from "@capacitor/push-notifications";
import {isNative} from "../utils";
import {BehaviorSubject, combineLatest, filter, switchMap, take} from "rxjs";
import typia from "typia";
import {UpdateNotificationChannelCommand} from "homey-housing-lottery-app-dto";
import {StorageService} from "./storage.service";
import {Router} from "@angular/router";
import {AnalyticsService} from "./analytics.service";
import {NotificationSettingsService} from "./notification-settings.service";
import {AppLauncher} from "@capacitor/app-launcher";

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  // messaging = getMessaging(this.firebaseApp);

  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);

  constructor(
    private readonly userService: UserService,
  ) {

  }

  initialize() {
    if (isNative) {
      this.setupPushNotificationsOnNative();
    }
  }

  async setupPushNotificationsOnNative() {
    await this.addListeners();
    await this.refreshDevicePushPermissionStatus();

    combineLatest([
      this.userService.user$,
      this.token$
    ])
      .pipe(
        filter(([user, token]) => user.status !== 'unauthorized' && token !== undefined),
        take(1),
        switchMap(
          ([,token]) => {
            console.log('Adding token to user', token);
            const command: UpdateNotificationChannelCommand = {
              type: "push",
              enabled: true,
              token,
            };

            return this.notificationSettingsService.updateChannel(
              typia.assert<UpdateNotificationChannelCommand>(command)
            );
          }
        )
      ).subscribe();
  }

  public async attemptRegisterPushNotifications() {
    if (isNative && !(await this.storageService.getItem('REQUESTED_INITIAL_PUSH_PERMISSION'))) {
      await this.storageService.setItem('REQUESTED_INITIAL_PUSH_PERMISSION', true);
      const result = await this.registerNotifications();
      return result.granted;
    } else {
      console.warn('Push notifications are not supported on this platform');
      return false;
    }
  }

  private readonly token$ = new BehaviorSubject<string | undefined>(undefined);
  private readonly devicePermissionSubject = new BehaviorSubject<boolean>(false);
  public readonly devicePushPermissionsEnabled$ = this.devicePermissionSubject.asObservable();
  private readonly notificationSettingsService = inject(NotificationSettingsService);

  private async addListeners() {
    await PushNotifications.addListener('registration', token => {
      console.info('Registration token: ', token.value);
      this.token$.next(token.value);
    });

    await PushNotifications.addListener('registrationError', err => {
      console.error('Registration error: ', err.error);
    });

    await PushNotifications.addListener('registrationError', err => {
      console.error('Registration error: ', err.error);
      this.token$.next(undefined);
    });

    await PushNotifications.addListener('pushNotificationReceived', notification => {
      console.log('Push notification received: ', notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
      const url = notification.notification.data?.url || notification.notification.data?.deeplink;
      const notificationType = notification.notification.data?.notificationType ?? 'unknown';

      this.analyticsService.sendEvent(
        'push-notification-clicked', notification.notification.data
      )
      this.analyticsService.sendEvent('click-tracked', {
        source: notificationType,
        type: 'push-notification',
      });

      if (!url) return;

      try {
        this.router.navigateByUrl(url);
      } catch {
        console.error('Failed to navigate to URL from push notification:', url);
      }
    });
  }

  async registerNotifications(): Promise<{ granted: boolean, status: 'granted' | 'denied' | 'prompt' }> {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    const granted = permStatus.receive === 'granted';
    this.devicePermissionSubject.next(granted);

    if (!granted) {
      this.token$.next(undefined);
      return {granted: false, status: permStatus.receive === 'denied' ? 'denied' : 'prompt'};
    }

    await PushNotifications.register();
    return {granted: true, status: permStatus.receive === 'granted' ? 'granted' : 'prompt'};
  }

  private async refreshDevicePushPermissionStatus() {
    if (!isNative) {
      this.devicePermissionSubject.next(false);
      return false;
    }

    const permStatus = await PushNotifications.checkPermissions();
    const granted = permStatus.receive === 'granted';
    this.devicePermissionSubject.next(granted);
    return granted;
  }

  public async openNotificationSettings() {
    try {
      await AppLauncher.openUrl({url: 'app-settings:'});
    } catch (error) {
      console.error('Failed to open app settings', error);
    }
  }
}
